import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave,
  faSpinner,
  faFolderOpen,
  faClock,
  faFileCsv,
  faStickyNote,
  faFileAlt,
  faClipboardList,
  faCheckCircle,
  faTrash,
  faUpload,
  faRedo,
} from '@fortawesome/free-solid-svg-icons';
import s from '../CaseFilePage.module.css';

interface ActionBarProps {
  dirty: boolean;
  handleSave: () => void;
  isSavePending: boolean;
  caseRef: string;
  caseData: Record<string, unknown>;
  canRecordTime: boolean;
  canExportData: boolean;
  canCreateTask: boolean;
  canDeleteCase: boolean;
  getFieldValue: (key: string) => unknown;
  setField: (key: string, value: unknown) => void;
  onRecordTime: () => void;
  onAddNote: () => void;
  onDraftAssistant: () => void;
  onAddTask: () => void;
  onCompleteTask: () => void;
  onDeleteCase: () => void;
  handleExportTimesheet: () => void;
  clioMatterId?: string | null;
  isClioSyncing?: boolean;
  onSyncToClio: () => void;
  onForceSyncToClio: () => void;
}

export default function ActionBar({
  dirty,
  handleSave,
  isSavePending,
  caseRef,
  caseData,
  canRecordTime,
  canExportData,
  canCreateTask,
  canDeleteCase,
  getFieldValue,
  setField,
  onRecordTime,
  onAddNote,
  onDraftAssistant,
  onAddTask,
  onCompleteTask,
  onDeleteCase,
  handleExportTimesheet,
  clioMatterId,
  isClioSyncing = false,
  onSyncToClio,
  onForceSyncToClio,
}: ActionBarProps) {
  return (
    <div className={s.actionBar}>
      <button
        className={`${s.actionBtn} ${dirty ? s.actionBtnSave : ''}`}
        onClick={handleSave}
        disabled={!dirty || isSavePending}
      >
        <FontAwesomeIcon icon={isSavePending ? faSpinner : faSave} spin={isSavePending} />
        {dirty ? 'Save Changes' : 'Saved'}
      </button>

      <div className={s.actionGroup}>
        <button
          className={s.actionBtn}
          data-tooltip="Documents"
          onClick={() => {
            const ref = (caseRef || '').trim();
            const base = String(caseData?.doc_base_directory || '').trim();
            const prefix = base ? `${base}/${ref}/` : `${ref}/`;
            window.open(
              `/cases/${encodeURIComponent(ref)}/explorer?prefix=${encodeURIComponent(prefix)}`,
              '_blank',
            );
          }}
        >
          <FontAwesomeIcon icon={faFolderOpen} />
          <span className={s.actionBtnLabel}> Documents</span>
        </button>
        {canRecordTime && (
          <button className={s.actionBtn} data-tooltip="Record Time" onClick={onRecordTime}>
            <FontAwesomeIcon icon={faClock} />
            <span className={s.actionBtnLabel}> Record Time</span>
          </button>
        )}
        <button
          className={s.actionBtn}
          data-tooltip={canExportData ? 'Timesheet' : 'Permission Blocked'}
          onClick={canExportData ? handleExportTimesheet : undefined}
          disabled={!canExportData}
          style={!canExportData ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        >
          <FontAwesomeIcon icon={faFileCsv} />
          <span className={s.actionBtnLabel}> Timesheet</span>
        </button>
        <button className={s.actionBtn} data-tooltip="Add Note" onClick={onAddNote}>
          <FontAwesomeIcon icon={faStickyNote} />
          <span className={s.actionBtnLabel}> Add Note</span>
        </button>
        <button className={s.actionBtn} data-tooltip="Draft Assistant" onClick={onDraftAssistant}>
          <FontAwesomeIcon icon={faFileAlt} />
          <span className={s.actionBtnLabel}> Draft Assistant</span>
        </button>
        {canCreateTask && (
          <button className={s.actionBtn} data-tooltip="Add Task" onClick={onAddTask}>
            <FontAwesomeIcon icon={faClipboardList} />
            <span className={s.actionBtnLabel}> Add Task</span>
          </button>
        )}
        <button className={s.actionBtn} data-tooltip="Complete Task" onClick={onCompleteTask}>
          <FontAwesomeIcon icon={faCheckCircle} />
          <span className={s.actionBtnLabel}> Complete Task</span>
        </button>
        {canDeleteCase && (
          <button
            className={`${s.actionBtn} ${s.actionBtnDanger}`}
            data-tooltip="Delete Case"
            onClick={onDeleteCase}
          >
            <FontAwesomeIcon icon={faTrash} />
            <span className={s.actionBtnLabel}> Delete Case</span>
          </button>
        )}
        {clioMatterId ? (
          <>
            <button
              className={s.actionBtn}
              data-tooltip={`Matter #${clioMatterId} — already in Clio`}
              disabled
              style={{ opacity: 0.7, cursor: 'default' }}
            >
              <FontAwesomeIcon icon={faCheckCircle} />
              <span className={s.actionBtnLabel}> In Clio</span>
            </button>
            <button
              className={s.actionBtn}
              data-tooltip="Re-sync to Clio — clears existing link and re-imports contact, matter, tasks and documents"
              onClick={onForceSyncToClio}
              disabled={isClioSyncing}
            >
              <FontAwesomeIcon icon={isClioSyncing ? faSpinner : faRedo} spin={isClioSyncing} />
              <span className={s.actionBtnLabel}>{isClioSyncing ? ' Syncing…' : ' Re-sync'}</span>
            </button>
          </>
        ) : (
          <button
            className={s.actionBtn}
            data-tooltip="Sync this case — contact, matter, and documents — to Clio"
            onClick={onSyncToClio}
            disabled={isClioSyncing}
          >
            <FontAwesomeIcon icon={isClioSyncing ? faSpinner : faUpload} spin={isClioSyncing} />
            <span className={s.actionBtnLabel}>
              {isClioSyncing ? ' Syncing…' : ' Sync to Clio'}
            </span>
          </button>
        )}
      </div>

      <label className={s.jointClaimLabel}>
        <input
          type="checkbox"
          checked={!!getFieldValue('joint_claim')}
          onChange={(e) => setField('joint_claim', e.target.checked)}
        />
        Joint Claim
      </label>
    </div>
  );
}
